import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  Tab,
  Tabs
} from '@mui/material'
import { ResponsiveTable, TableEmptyState } from './TableComponents'
import CloseIcon from '@mui/icons-material/Close'
import GetAppIcon from '@mui/icons-material/GetApp'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import styles from './ImportResultsDialog.module.scss'

function escapeCSVField (value) {
  if (value == null) return ''
  let str = String(value)
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str
  }
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"'
  }
  return str
}

const ImportResultsDialog = ({ open, onClose, result = null }) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)

  if (!result || !result.rows) return null

  const { rows } = result
  const tabData = [
    { label: `${t('importQA.imported')} (${rows.imported.length})`, rows: rows.imported, type: 'imported' },
    { label: `${t('importQA.updated')} (${rows.updated.length})`, rows: rows.updated, type: 'updated' },
    { label: `${t('importQA.skipped')} (${rows.skipped.length})`, rows: rows.skipped, type: 'skipped' },
    { label: `${t('importQA.errors')} (${rows.errored.length})`, rows: rows.errored, type: 'errored' }
  ]
  const currentTab = tabData[activeTab]

  const handleExportCSV = () => {
    const headers = [
      t('importQA.table.row'), 'Status', t('importQA.table.callId'), t('importQA.table.campaign'),
      t('importQA.table.agent'), t('importQA.table.date'), t('importQA.table.grade'),
      t('importQA.table.result'), `${t('importQA.table.reason')}/${t('importQA.table.error')}`
    ]
    const allRows = [
      ...rows.imported.map(r => ({ ...r, status: t('importQA.imported'), detail: '' })),
      ...rows.updated.map(r => ({ ...r, status: t('importQA.updated'), detail: '' })),
      ...rows.skipped.map(r => ({ ...r, status: t('importQA.skipped'), detail: r.reason || '' })),
      ...rows.errored.map(r => ({ ...r, status: t('importQA.errors'), detail: r.error || '' }))
    ].sort((a, b) => (a.rowIndex || 0) - (b.rowIndex || 0))

    const csvLines = [
      headers.map(escapeCSVField).join(','),
      ...allRows.map(r => [
        r.rowIndex,
        r.status,
        r.callId,
        r.campaignName,
        r.agentEmail,
        r.callDate,
        r.callGrade,
        r.result,
        r.detail
      ].map(escapeCSVField).join(','))
    ]

    const csvContent = csvLines.join('\r\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `qa-import-results-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const baseColumns = [
    { id: 'rowIndex', label: t('importQA.table.row'), renderCell: (row) => row.rowIndex },
    { id: 'callId', label: t('importQA.table.callId'), renderCell: (row) => row.callId || '-' },
    { id: 'campaignName', label: t('importQA.table.campaign'), renderCell: (row) => row.campaignName || '-' },
    { id: 'agentEmail', label: t('importQA.table.agent'), renderCell: (row) => row.agentEmail || '-' },
    { id: 'callDate', label: t('importQA.table.date'), renderCell: (row) => row.callDate || '-' },
    { id: 'callGrade', label: t('importQA.table.grade'), renderCell: (row) => row.callGrade || '-' },
    { id: 'result', label: t('importQA.table.result'), renderCell: (row) => row.result || '-' }
  ]

  const columns = [
    ...baseColumns,
    ...(currentTab.type === 'skipped'
      ? [{ id: 'reason', label: t('importQA.table.reason'), renderCell: (row) => row.reason || '-' }]
      : []),
    ...(currentTab.type === 'errored'
      ? [{ id: 'error', label: t('importQA.table.error'), renderCell: (row) => row.error || '-' }]
      : [])
  ]

  const renderTable = () => (
    <ResponsiveTable
      columns={columns}
      data={currentTab.rows}
      variant='qa'
      emptyState={
        <TableEmptyState
          message={t('importQA.dialog.noRows')}
        />
      }
      containerProps={{ className: styles.tableContainer }}
    />
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      className={styles.dialog}
      aria-labelledby='import-results-dialog-title'
    >
      <DialogTitle disableTypography className={styles.dialogTitle}>
        <div className={styles.titleContent}>
          <CheckCircleIcon className={styles.titleIcon} />
          <Typography className={styles.titleText}>{t('importQA.dialog.title')}</Typography>
        </div>
        <IconButton
          className={styles.closeButton}
          onClick={onClose}
          aria-label='close'
          size='small'
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: '16px 24px 24px' }}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Box className={styles.statItem}>
              <Typography className={styles.statLabel}>{t('importQA.imported')}</Typography>
              <Typography className={styles.statValue} style={{ color: 'var(--color-success)' }}>
                {result.imported}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box className={styles.statItem}>
              <Typography className={styles.statLabel}>{t('importQA.updated')}</Typography>
              <Typography className={styles.statValue} style={{ color: 'var(--color-text-primary)' }}>
                {result.updated}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box className={styles.statItem}>
              <Typography className={styles.statLabel}>{t('importQA.skipped')}</Typography>
              <Typography className={styles.statValue} style={{ color: 'var(--color-text-secondary)' }}>
                {result.skipped}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box className={styles.statItem}>
              <Typography className={styles.statLabel}>{t('importQA.errors')}</Typography>
              <Typography className={styles.statValue} style={{ color: result.errors.length > 0 ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>
                {result.errors.length}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          className={styles.tabs}
          variant='fullWidth'
        >
          {tabData.map((tab, i) => (
            <Tab key={i} label={tab.label} />
          ))}
        </Tabs>

        {renderTable()}
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button
          variant='outlined'
          startIcon={<GetAppIcon />}
          onClick={handleExportCSV}
          className={styles.exportButton}
        >
          {t('importQA.dialog.exportCsv')}
        </Button>
        <Button
          variant='contained'
          onClick={onClose}
          className={styles.closeActionButton}
        >
          {t('importQA.dialog.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ImportResultsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  result: PropTypes.shape({
    imported: PropTypes.number,
    updated: PropTypes.number,
    skipped: PropTypes.number,
    errors: PropTypes.array,
    rows: PropTypes.shape({
      imported: PropTypes.array,
      updated: PropTypes.array,
      skipped: PropTypes.array,
      errored: PropTypes.array
    })
  })
}

export default ImportResultsDialog
